import React, { useState, useEffect } from 'react';
import { fetchZones, fetchUnits, submitForm, checkSubmission } from './services/api';
import './App.css';

// Days of the week in Malayalam
const DAYS_OF_WEEK = [
  'ഞായർ',
  'തിങ്കൾ',
  'ചൊവ്വ',
  'ബുധൻ',
  'വ്യാഴം',
  'വെള്ളി',
  'ശനി',
];

function App() {
  // Form state
  const [formData, setFormData] = useState({
    zoneName: '',
    unitName: '',
    qhlsStatus: '', // 'yes' or 'no'
    qhlsDay: '',
    faculty: '',
    facultyMobile: '',
    syllabus: '',
    sthalam: '',
    afterRamadhan: '',
    gentsCount: '',
    ladiesCount: '',
  });

  // Dropdown options
  const [zones, setZones] = useState([]);
  const [units, setUnits] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Success popup state
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({ status: '', zone: '', unit: '' });
  
  // Update confirmation popup state
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [isForceAppend, setIsForceAppend] = useState(false);

  // Fetch zones on mount
  useEffect(() => {
    loadZones();
  }, []);

  // Fetch units when zone changes
  useEffect(() => {
    if (formData.zoneName) {
      loadUnits(formData.zoneName);
    } else {
      setUnits([]);
    }
    // Reset update mode when zone changes
    setIsUpdateMode(false);
    setIsForceAppend(false);
    setExistingData(null);
  }, [formData.zoneName]);

  // Check for existing submission when unit changes
  useEffect(() => {
    if (formData.zoneName && formData.unitName && !isUpdateMode) {
      checkExistingSubmission(formData.zoneName, formData.unitName);
    }
  }, [formData.unitName]);

  async function checkExistingSubmission(zone, unit) {
    try {
      const data = await checkSubmission(zone, unit);
      if (data.exists) {
        setExistingData(data.submission);
        setShowUpdatePopup(true);
      }
    } catch (error) {
      console.error('Error checking submission:', error);
    }
  }

  async function loadZones() {
    try {
      setZonesLoading(true);
      const data = await fetchZones();
      setZones(data);
    } catch (error) {
      setMessage({ type: 'error', text: 'മണ്ഡലങ്ങൾ ലോഡ് ചെയ്യാനായില്ല. പേജ് പുതുക്കുക.' });
    } finally {
      setZonesLoading(false);
    }
  }

  async function loadUnits(zone) {
    try {
      setUnitsLoading(true);
      setFormData(prev => ({ ...prev, unitName: '' }));
      const data = await fetchUnits(zone);
      setUnits(data);
    } catch (error) {
      setMessage({ type: 'error', text: 'ശാഖകൾ ലോഡ് ചെയ്യാനായില്ല.' });
    } finally {
      setUnitsLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear message when user types
    if (message.text) setMessage({ type: '', text: '' });
  }

  function handleStatusChange(status) {
    setFormData(prev => ({
      ...prev,
      qhlsStatus: status,
      // Clear QHLS fields if "no" is selected
      ...(status === 'no' ? {
        qhlsDay: '',
        faculty: '',
        facultyMobile: '',
        syllabus: '',
        sthalam: '',
        afterRamadhan: '',
        gentsCount: '',
        ladiesCount: '',
      } : {})
    }));
    if (message.text) setMessage({ type: '', text: '' });
  }

  function validateForm() {
    const { zoneName, unitName, qhlsStatus, qhlsDay, faculty, syllabus, sthalam, afterRamadhan, gentsCount, ladiesCount } = formData;
    
    if (!zoneName) return 'ദയവായി മണ്ഡലം തിരഞ്ഞെടുക്കുക';
    if (!unitName) return 'ദയവായി ശാഖ തിരഞ്ഞെടുക്കുക';
    if (!qhlsStatus) return 'ദയവായി QHLS സ്റ്റാറ്റസ് തിരഞ്ഞെടുക്കുക';
    
    // Only validate QHLS fields if QHLS was conducted
    if (qhlsStatus === 'yes') {
      if (!qhlsDay) return 'ദയവായി QHLS ദിവസം തിരഞ്ഞെടുക്കുക';
      if (!faculty.trim()) return 'ദയവായി ഫാക്കൽറ്റിയുടെ പേര് നൽകുക';
      
      const mobileRegex = /^[0-9]{10}$/;
      if (!mobileRegex.test(formData.facultyMobile)) return 'ദയവായി 10 അക്ക മൊബൈൽ നമ്പർ നൽകുക';
      
      if (!syllabus.trim()) return 'ദയവായി സിലബസ് നൽകുക';
      if (!sthalam.trim()) return 'ദയവായി സ്ഥലം നൽകുക';
      if (!afterRamadhan) return 'റമദാനിന് ശേഷം QHLS നടക്കുന്നുണ്ടോ എന്ന് തിരഞ്ഞെടുക്കുക';
      
      if (gentsCount === '' || gentsCount < 0) return 'ദയവായി പുരുഷന്മാരുടെ എണ്ണം നൽകുക';
      if (ladiesCount === '' || ladiesCount < 0) return 'ദയവായി സ്ത്രീകളുടെ എണ്ണം നൽകുക';
    }
    
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    const error = validateForm();
    if (error) {
      setMessage({ type: 'error', text: error });
      return;
    }

    // Store zone and unit names before resetting
    const submittedZone = formData.zoneName;
    const submittedUnit = formData.unitName;
    const submittedStatus = formData.qhlsStatus;

    try {
      setLoading(true);
      await submitForm({
        ...formData,
        isUpdate: isUpdateMode,
        forceAppend: isForceAppend,
        gentsCount: formData.qhlsStatus === 'yes' ? (parseInt(formData.gentsCount) || 0) : 0,
        ladiesCount: formData.qhlsStatus === 'yes' ? (parseInt(formData.ladiesCount) || 0) : 0,
      });
      
      // Show success popup
      setPopupData({
        status: submittedStatus,
        zone: submittedZone,
        unit: submittedUnit,
      });
      setShowPopup(true);
      
      // Reset form
      setFormData({
        zoneName: '',
        unitName: '',
        qhlsStatus: '',
        qhlsDay: '',
        faculty: '',
        facultyMobile: '',
        syllabus: '',
        sthalam: '',
        afterRamadhan: '',
        gentsCount: '',
        ladiesCount: '',
      });
      setUnits([]);
      setIsUpdateMode(false);
      setIsForceAppend(false);
      setExistingData(null);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'സേവ് ചെയ്യാനായില്ല. വീണ്ടും ശ്രമിക്കുക.' });
    } finally {
      setLoading(false);
    }
  }

  function closePopup() {
    setShowPopup(false);
    setPopupData({ status: '', zone: '', unit: '' });
  }

  function handleUpdateConfirm() {
    if (!existingData) return;
    
    // Pre-fill form with existing data
    setFormData({
      zoneName: existingData.zone,
      unitName: existingData.unit,
      qhlsStatus: existingData.status === 'QHLS ഉണ്ട്' ? 'yes' : 'no',
      qhlsDay: existingData.day || '',
      faculty: existingData.faculty || '',
      facultyMobile: existingData.facultyMobile || '',
      syllabus: existingData.syllabus || '',
      sthalam: existingData.sthalam || '',
      afterRamadhan: existingData.afterRamadhan || '',
      gentsCount: existingData.gents || 0,
      ladiesCount: existingData.ladies || 0,
    });
    
    setIsUpdateMode(true);
    setShowUpdatePopup(false);
  }

  function handleUpdateCancel() {
    setShowUpdatePopup(false);
    setFormData(prev => ({ ...prev, unitName: '' }));
  }

  function handleAddAnother() {
    setShowPopup(false);
    // Keep zone and unit, clear everything else
    setFormData(prev => ({
      ...prev,
      qhlsStatus: '',
      qhlsDay: '',
      faculty: '',
      facultyMobile: '',
      syllabus: '',
      sthalam: '',
      afterRamadhan: '',
      gentsCount: '',
      ladiesCount: '',
    }));
    setIsUpdateMode(false);
    setIsForceAppend(true);
    setExistingData(null);
  }

  function handleAddNew() {
    setShowUpdatePopup(false);
    setIsUpdateMode(false);
    setIsForceAppend(true);
    // Just clear fields but keep unit
    setFormData(prev => ({
      ...prev,
      qhlsStatus: '',
      qhlsDay: '',
      faculty: '',
      facultyMobile: '',
      syllabus: '',
      sthalam: '',
      afterRamadhan: '',
      gentsCount: '',
      ladiesCount: '',
    }));
  }

  const totalParticipants = (parseInt(formData.gentsCount) || 0) + (parseInt(formData.ladiesCount) || 0);
  const showQhlsFields = formData.qhlsStatus === 'yes';

  return (
    <div className="app">
      {/* Success Popup Modal */}
      {showPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-modal" onClick={(e) => e.stopPropagation()}>
            <p className="popup-received">താങ്കളുടെ മറുപടി സ്വീകരിച്ചിരിക്കുന്നു</p>
            {popupData.status === 'yes' ? (
              <>
                <div className="popup-icon success">✓</div>
                <h2 className="popup-arabic">جَزَاكَ اللهُ خَيْرًا</h2>
                <p className="popup-message">
                  <strong>{popupData.zone}</strong> മണ്ഡലത്തിൽ <strong>{popupData.unit}</strong> ശാഖയിൽ 
                  QHLS നിലനിർത്തുവാനും പങ്കാളിത്തം കൂട്ടുവാനും അല്ലാഹു തൗഫീഖ് നൽകുമാറാവട്ടെ.
                </p>
              </>
            ) : (
              <>
                <div className="popup-icon encourage">🤲</div>
                <p className="popup-message">
                  <strong>{popupData.zone}</strong> മണ്ഡലത്തിൽ <strong>{popupData.unit}</strong> ശാഖയിൽ 
                  QHLS തുടങ്ങാൻ അല്ലാഹു തൗഫീഖ് നൽകുമാറാവട്ടെ.
                </p>
              </>
            )}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="popup-close-btn" style={{ background: '#334155', flex: 1 }} onClick={closePopup}>
                ശരി
              </button>
              {popupData.status === 'yes' && (
                <button className="popup-close-btn" style={{ flex: 1.5 }} onClick={handleAddAnother}>
                  മറ്റൊരു QHLS കൂടി ഉണ്ട്
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Update Confirmation Modal */}
      {showUpdatePopup && (
        <div className="popup-overlay">
          <div className="popup-modal">
            <div className="popup-icon encourage">📝</div>
            <p className="popup-message">
              <strong>{formData.unitName}</strong> ശാഖയുടെ വിവരങ്ങൾ നേരത്തെ തന്നെ ഫിൽ ചെയ്തിട്ടുണ്ട്. ഇത് അപ്ഡേറ്റ് ചെയ്യണോ?
            </p>

            {existingData && (
              <div className="update-summary">
                <div className="summary-item">
                  <span className="summary-label">സ്റ്റാറ്റസ്:</span>
                  <span className="summary-value">{existingData.status}</span>
                </div>
                {existingData.status === 'QHLS ഉണ്ട്' && (
                  <>
                    <div className="summary-item">
                      <span className="summary-label">ദിവസം:</span>
                      <span className="summary-value">{existingData.day}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">ഫാക്കൽറ്റി:</span>
                      <span className="summary-value">{existingData.faculty}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">പങ്കാളിത്തം:</span>
                      <span className="summary-value">
                        {existingData.gents} + {existingData.ladies} ({existingData.gents + existingData.ladies})
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  className="popup-close-btn" 
                  style={{ background: '#334155', flex: 1 }} 
                  onClick={handleUpdateCancel}
                >
                  വേണ്ട
                </button>
                <button 
                  className="popup-close-btn" 
                  style={{ flex: 1 }} 
                  onClick={handleUpdateConfirm}
                >
                  അപ്ഡേറ്റ് ചെയ്യണം
                </button>
              </div>
              {existingData && existingData.status === 'QHLS ഉണ്ട്' && (
                <button 
                  className="popup-close-btn" 
                  style={{ background: '#0d9488' }} 
                  onClick={handleAddNew}
                >
                  പുതിയ QHLS ചേർക്കുക
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="form-container">
        <div className="form-header">
          <h1>QHLS Data Collection</h1>
          <p>Submit your unit's QHLS program details</p>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Zone Dropdown */}
          <div className="form-group">
            <label htmlFor="zoneName">മണ്ഡലം <span className="required">*</span></label>
            <select
              id="zoneName"
              name="zoneName"
              value={formData.zoneName}
              onChange={handleChange}
              disabled={zonesLoading}
            >
              <option value="">
                {zonesLoading ? 'ലോഡ് ചെയ്യുന്നു...' : 'മണ്ഡലം തിരഞ്ഞെടുക്കുക'}
              </option>
              {zones.map(zone => (
                <option key={zone} value={zone}>{zone}</option>
              ))}
            </select>
          </div>

          {/* Unit Dropdown */}
          <div className="form-group">
            <label htmlFor="unitName">ശാഖ <span className="required">*</span></label>
            <select
              id="unitName"
              name="unitName"
              value={formData.unitName}
              onChange={handleChange}
              disabled={!formData.zoneName || unitsLoading}
            >
              <option value="">
                {unitsLoading ? 'ലോഡ് ചെയ്യുന്നു...' : 
                 !formData.zoneName ? 'ആദ്യം മണ്ഡലം തിരഞ്ഞെടുക്കുക' : 'ശാഖ തിരഞ്ഞെടുക്കുക'}
              </option>
              {units.map(unit => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
          </div>

          {/* QHLS Status Radio Buttons */}
          <div className="form-group">
            <label>QHLS സ്റ്റാറ്റസ് <span className="required">*</span></label>
            <div className="radio-group">
              <label className={`radio-option ${formData.qhlsStatus === 'yes' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="qhlsStatus"
                  value="yes"
                  checked={formData.qhlsStatus === 'yes'}
                  onChange={() => handleStatusChange('yes')}
                />
                <span className="radio-label">QHLS ഉണ്ട്</span>
              </label>
              <label className={`radio-option ${formData.qhlsStatus === 'no' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="qhlsStatus"
                  value="no"
                  checked={formData.qhlsStatus === 'no'}
                  onChange={() => handleStatusChange('no')}
                />
                <span className="radio-label">QHLS ഇല്ല</span>
              </label>
            </div>
          </div>

          {/* Conditional QHLS Fields - Only show if QHLS ഉണ്ട് is selected */}
          {showQhlsFields && (
            <>
              {/* QHLS Day */}
              <div className="form-group">
                <label htmlFor="qhlsDay">ദിവസം <span className="required">*</span></label>
                <select
                  id="qhlsDay"
                  name="qhlsDay"
                  value={formData.qhlsDay}
                  onChange={handleChange}
                >
                  <option value="">ദിവസം തിരഞ്ഞെടുക്കുക</option>
                  {DAYS_OF_WEEK.map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>

              {/* Faculty */}
              <div className="form-group">
                <label htmlFor="faculty">ഫാക്കൽറ്റി <span className="required">*</span></label>
                <input
                  type="text"
                  id="faculty"
                  name="faculty"
                  value={formData.faculty}
                  onChange={handleChange}
                  placeholder="ഫാക്കൽറ്റിയുടെ പേര് നൽകുക"
                />
              </div>

              {/* Faculty Mobile */}
              <div className="form-group">
                <label htmlFor="facultyMobile">ഫാക്കൽറ്റി മൊബൈൽ നമ്പർ <span className="required">*</span></label>
                <input
                  type="tel"
                  id="facultyMobile"
                  name="facultyMobile"
                  value={formData.facultyMobile}
                  onChange={handleChange}
                  placeholder="10 അക്ക നമ്പർ"
                  maxLength="10"
                />
              </div>

              {/* Syllabus */}
              <div className="form-group">
                <label htmlFor="syllabus">സിലബസ് (സൂറത്തിന്റെ പേര്) <span className="required">*</span></label>
                <input
                  type="text"
                  id="syllabus"
                  name="syllabus"
                  value={formData.syllabus}
                  onChange={handleChange}
                  placeholder="സിലബസ് നൽകുക"
                />
              </div>

              {/* Sthalam */}
              <div className="form-group">
                <label htmlFor="sthalam">സ്ഥലം <span className="required">*</span></label>
                <input
                  type="text"
                  id="sthalam"
                  name="sthalam"
                  value={formData.sthalam}
                  onChange={handleChange}
                  placeholder="സ്ഥലം നൽകുക"
                />
              </div>

              {/* After Ramadhan Status */}
              <div className="form-group">
                <label>റമദാനിന് ശേഷം QHLS നടക്കുന്നുണ്ടോ? <span className="required">*</span></label>
                <div className="radio-group">
                  <label className={`radio-option ${formData.afterRamadhan === 'yes' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="afterRamadhan"
                      value="yes"
                      checked={formData.afterRamadhan === 'yes'}
                      onChange={handleChange}
                    />
                    <span className="radio-label">ഉണ്ട്</span>
                  </label>
                  <label className={`radio-option ${formData.afterRamadhan === 'no' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="afterRamadhan"
                      value="no"
                      checked={formData.afterRamadhan === 'no'}
                      onChange={handleChange}
                    />
                    <span className="radio-label">ഇല്ല</span>
                  </label>
                </div>
              </div>

              {/* Participant Counts */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="gentsCount">പുരുഷന്മാർ <span className="required">*</span></label>
                  <input
                    type="number"
                    id="gentsCount"
                    name="gentsCount"
                    value={formData.gentsCount}
                    onChange={handleChange}
                    min="0"
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ladiesCount">സ്ത്രീകൾ <span className="required">*</span></label>
                  <input
                    type="number"
                    id="ladiesCount"
                    name="ladiesCount"
                    value={formData.ladiesCount}
                    onChange={handleChange}
                    min="0"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Total Display */}
              {(formData.gentsCount || formData.ladiesCount) && (
                <div className="total-display">
                  ആകെ പങ്കാളികൾ: <strong>{totalParticipants}</strong>
                </div>
              )}
            </>
          )}

          {/* Submit Button */}
          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading ? 'സേവ് ചെയ്യുന്നു...' : 'സേവ് ചെയ്യുക'}
          </button>
        </form>

        <div className="form-footer">
          <p><span className="required">*</span> അടയാളപ്പെടുത്തിയ എല്ലാ ഫീൽഡുകളും നിർബന്ധമാണ്</p>
        </div>
      </div>
    </div>
  );
}

export default App;
